import React from 'react';
import {
  Box,
  CardHeader,
  CardContent,
  Chip,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

interface Unit {
  _id?: string;
  category?: string;
  unitType?: string;
  quantity?: number;
}

interface ACDetail {
  _id: string;
  acType: string;
  quantity: number;
}

interface Quotation {
  amount?: number;
  notes?: string;
  sharedAt?: string;
}

interface AmcProperty {
  propertySubType?: string;
  address?: string;
  units?: Unit[];
  totalUnits?: number;
}

interface Props {
  quantity?: number;
  place?: string;
  comment?: string;
  acDetails?: ACDetail[];
  amcEnquiryDetails?: any;
  properties?: AmcProperty[];
}

const PURPLE = '#7B1FA2';
const GREEN = '#0BE600';
const MUTED = '#8A8D91';

const humanize = (v: any) =>
  v === null || v === undefined || v === ''
    ? 'N/A'
    : String(v)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <>
    <Grid item xs={4} md={3} sx={{ color: MUTED }}>
      {label}
    </Grid>
    <Grid item xs={8} md={9}>
      <Typography variant="body2" sx={{ color: '#fff' }}>
        {value}
      </Typography>
    </Grid>
  </>
);

const AMCRequestDetailsCard = ({
  comment,
  acDetails = [],
  amcEnquiryDetails,
  properties = [],
}: Props) => {
  const amc = amcEnquiryDetails || {};
  const units: Unit[] = Array.isArray(amc.units) ? amc.units : [];
  const quotation: Quotation | null = amc.quotation || null;

  const hasAmc = amc && Object.keys(amc).length > 0;
  const rows: { acType: string; quantity: number }[] =
    units.length > 0
      ? units.map((u) => ({
          acType: `${humanize(u.category)}${u.unitType ? ` · ${u.unitType}` : ''}`,
          quantity: u.quantity || 0,
        }))
      : acDetails.map((a) => ({ acType: a.acType, quantity: a.quantity }));

  return (
    <Box sx={{ color: '#fff' }}>
      {/* ── AMC Plan attributes ─────────────────────────────── */}
      {hasAmc && (
        <>
          <CardHeader
            title="AMC Plan"
            titleTypographyProps={{ fontWeight: 600, fontSize: 18 }}
            action={
              amc.bookingReference ? (
                <Chip
                  label={amc.bookingReference}
                  sx={{
                    backgroundColor: 'rgba(123,31,162,0.16)',
                    color: PURPLE,
                    fontWeight: 600,
                    mt: 1,
                    mr: 1,
                  }}
                />
              ) : null
            }
          />
          <Divider sx={{ borderColor: '#565656' }} />
          <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
            <Grid container spacing={1.5} rowSpacing={2}>
              <DetailRow
                label="Property Category"
                value={humanize(amc.propertyCategory)}
              />
              {amc.propertySubType && (
                <DetailRow
                  label="Property Type"
                  value={humanize(amc.propertySubType)}
                />
              )}
              <DetailRow
                label="Service Level"
                value={
                  <>
                    {humanize(amc.serviceLevel)}
                    {amc.serviceLevelAddOn ? (
                      <span style={{ color: MUTED }}>
                        {'  '}(+£{amc.serviceLevelAddOn})
                      </span>
                    ) : null}
                  </>
                }
              />
              <DetailRow
                label="Payment Preference"
                value={humanize(amc.paymentPreference)}
              />
              <DetailRow label="Proceed Type" value={humanize(amc.proceedType)} />
              <DetailRow
                label="Include Boiler"
                value={
                  <Chip
                    size="small"
                    label={amc.includeBoiler ? 'Yes' : 'No'}
                    sx={{
                      backgroundColor: amc.includeBoiler
                        ? 'rgba(11,230,0,0.14)'
                        : 'rgba(138,141,145,0.14)',
                      color: amc.includeBoiler ? GREEN : MUTED,
                      fontWeight: 600,
                    }}
                  />
                }
              />
              <DetailRow label="No. of Sites" value={amc.noOfSites ?? 1} />
              <DetailRow
                label="Total Units"
                value={amc.totalUnits ?? rows.reduce((s, r) => s + r.quantity, 0)}
              />
              {amc.multiUnitDiscountPercent ? (
                <DetailRow
                  label="Multi-unit Discount"
                  value={`${amc.multiUnitDiscountPercent}%`}
                />
              ) : null}
              {amc.invoiceTerms && (
                <DetailRow
                  label="Invoice Terms"
                  value={humanize(amc.invoiceTerms)}
                />
              )}
            </Grid>
          </CardContent>
          <Divider sx={{ borderColor: '#565656' }} />
        </>
      )}

      {/* ── Per-property breakdown (multi-site contracts) ───── */}
      {properties.length > 1 && (
        <>
          <CardHeader
            title={`Properties (${properties.length})`}
            titleTypographyProps={{ fontWeight: 600, fontSize: 18 }}
          />
          <Divider sx={{ borderColor: '#565656' }} />
          <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
            <Grid container spacing={2}>
              {properties.map((property, index) => (
                <Grid item xs={12} key={index}>
                  <Paper
                    elevation={0}
                    sx={{
                      backgroundColor: '#2E2E2E',
                      borderRadius: 2,
                      p: 2,
                      border: '1px solid #3E3E3E',
                    }}
                  >
                    <Typography sx={{ color: '#fff', fontWeight: 600, mb: 0.5 }}>
                      Property {index + 1}
                      {property.propertySubType
                        ? ` · ${humanize(property.propertySubType)}`
                        : ''}
                    </Typography>
                    <Typography variant="body2" sx={{ color: MUTED, mb: 1 }}>
                      {property.address || 'No address provided'}
                    </Typography>
                    {(property.units?.length ?? 0) > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {property.units!.map((u, unitIndex) => (
                          <Chip
                            key={unitIndex}
                            size="small"
                            label={`${humanize(u.category)}${u.unitType ? ` · ${u.unitType}` : ''} × ${u.quantity || 0}`}
                            sx={{
                              backgroundColor: 'rgba(11,230,0,0.14)',
                              color: GREEN,
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: MUTED }}>
                        No unit details provided
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
          <Divider sx={{ borderColor: '#565656' }} />
        </>
      )}

      {/* ── Units / AC Details ──────────────────────────────── */}
      <CardHeader
        title="Unit Details"
        titleTypographyProps={{ fontWeight: 600, fontSize: 18 }}
      />
      <Divider sx={{ borderColor: '#565656' }} />
      <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
        {rows.length === 0 ? (
          <Typography variant="body2" sx={{ color: MUTED }}>
            No unit details provided
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: MUTED, borderColor: '#3E3E3E' }}>
                  Unit
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: MUTED, borderColor: '#3E3E3E' }}
                >
                  Quantity
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i}>
                  <TableCell
                    sx={{
                      color: GREEN,
                      fontWeight: 600,
                      borderColor: '#3E3E3E',
                      textTransform: 'capitalize',
                    }}
                  >
                    {r.acType}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: '#fff', borderColor: '#3E3E3E' }}
                  >
                    {r.quantity} Unit{r.quantity > 1 ? 's' : ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {comment && (
          <>
            <Divider sx={{ my: 3, borderColor: '#565656' }} />
            <Typography variant="subtitle2" sx={{ mb: 1, color: MUTED }}>
              Summary
            </Typography>
            <Typography variant="body2" sx={{ color: '#ddd' }}>
              {comment}
            </Typography>
          </>
        )}
      </CardContent>
      <Divider sx={{ borderColor: '#565656' }} />

      {/* ── Shared quotation (if any) ───────────────────────── */}
      {quotation && (
        <>
          <CardHeader
            title="Quotation Shared"
            titleTypographyProps={{ fontWeight: 600, fontSize: 18 }}
          />
          <Divider sx={{ borderColor: '#565656' }} />
          <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
            <Paper
              elevation={0}
              sx={{
                backgroundColor: '#2E2E2E',
                borderRadius: 2,
                p: 2,
                border: `1px solid ${PURPLE}`,
              }}
            >
              <Typography sx={{ color: '#fff', fontSize: 22, fontWeight: 700 }}>
                £{quotation.amount}
              </Typography>
              {quotation.notes && (
                <Typography variant="body2" sx={{ color: '#ddd', mt: 1 }}>
                  {quotation.notes}
                </Typography>
              )}
            </Paper>
          </CardContent>
          <Divider sx={{ borderColor: '#565656' }} />
        </>
      )}
    </Box>
  );
};

export default AMCRequestDetailsCard;
